import './MapOverview.css';
import React, {useEffect, useRef, useState} from 'react';
import MapComponent from './MapComponent';
import BuildingListItem from './BuildingListItem';
import {useDocumentTitle} from "../hooks/useDocumentTitle";
import CustomDropdown from "../Components/Dropdown";
import FilterPanel from "./FilterPanel";
import {useMapOverview} from "../hooks/mapOverview";
import {SortOption} from "../hooks/mapOverview/buildingSort";
import {BuildingSummary} from "../types/protocols";
import {useIsSmallScreen} from "../hooks/useIsSmallScreen";
import {isNewTabClick, useOpenPath} from "../hooks/useOpenPath";

const SORT_SHORT_LABELS: Record<SortOption, string> = {
    name_asc: 'Abc',
    building_type: 'Typ',
    group_by_tag: 'Grupp',
};

export interface MapOverviewProps {}

const MapOverview: React.FC<MapOverviewProps> = () => {
    // ── Hook: all filter state, config, derived data ────────────
    const {
        locationCategories,
        buildings,
        filteredBuildings,
        buildingGroups,
        selectedCategory1,
        selectedCategory2,
        selectedCategory3,
        selectedCategory4,
        selectedCategory5,
        selectedBuildingTypes,
        selectedGroupTagKeys,
        selectedSort,
        selectCategory,
        setSelectedBuildingTypes,
        setSelectedGroupTagKeys,
        setSelectedSort,
        resetAllFilters,
        hasAnyFilters,
        categoryOptions,
        buildingTypeOptions,
        groupTagOptions,
        sortOptions,
        filteredCoordinates,
        allCoordinates,
    } = useMapOverview();

    // Set document title
    useDocumentTitle('Molnfastigheter');

    // Phones get the list only: the map is not rendered, and tapping a card
    // opens the building instead of highlighting it on the map. On larger
    // screens the first click highlights, a click on the highlighted card opens.
    const isSmallScreen = useIsSmallScreen();
    const openPath = useOpenPath();

    // ── UI-local state ──────────────────────────────────────────
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [highlightedKey, setHighlightedKey] = useState<string>('');
    const listItemRefs = useRef<{ [key: string]: HTMLLIElement | null }>({});
    const isSelectingNewBuildingRef = useRef<boolean>(false);
    const [popupInfo, setPopupInfo] = useState<{
        id: string;
        coordinates: [number, number];
    } | null>(null);
    const [shouldRefocusMap, setShouldRefocusMap] = useState(true);

    // ── Scroll highlighted item into view ───────────────────────
    useEffect(() => {
        if (!highlightedKey) return;
        const element = listItemRefs.current[highlightedKey];
        if (!element) return;
        const rect = element.getBoundingClientRect();
        const scroller = element.closest<HTMLElement>('.map-sidebar-scroll');
        const bounds = scroller?.getBoundingClientRect();
        const isInView = bounds
            ? rect.top >= bounds.top && rect.bottom <= bounds.bottom
            : rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

        if (!isInView) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [highlightedKey]);

    // ── Filter change handlers (also refocus the map on any change) ─
    const handleSelectCategory = (level: number, key: string) => {
        selectCategory(level, key);
        setShouldRefocusMap(true);
    };
    const handleBuildingTypesChange = (value: typeof selectedBuildingTypes) => {
        setSelectedBuildingTypes(value);
        setShouldRefocusMap(true);
    };
    const handleGroupTagKeysChange = (value: typeof selectedGroupTagKeys) => {
        setSelectedGroupTagKeys(value);
        setShouldRefocusMap(true);
    };
    const handleResetAllFilters = () => {
        resetAllFilters();
        setShouldRefocusMap(true);
    };

    // ── Building list item helpers ──────────────────────────────
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (building: BuildingSummary) =>
        (building.name || 'Namnlös byggnad').toLowerCase().includes(searchLower);

    // `reactKey` differs from `building.key` only in grouped mode, where the same
    // building can appear under several tag sections and needs a unique key.
    const renderBuildingItem = (building: BuildingSummary, reactKey: string) => (
        <BuildingListItem
            key={reactKey}
            building={building}
            highlighted={highlightedKey === building.key}
            showOpenButton={!isSmallScreen}
            onClick={(_key, event) => {
                // Cmd/Ctrl/middle click opens the building in a new tab at once;
                // a plain click highlights first and opens on the second click.
                if (isNewTabClick(event) || isSmallScreen || highlightedKey === building.key) {
                    openPath(`/building/${building.key}`, event);
                    return;
                }
                setShouldRefocusMap(false);
                isSelectingNewBuildingRef.current = true;
                setHighlightedKey(building.key!);
                if (building.coordinates?.latitude) {
                    setPopupInfo({
                        id: building.key!,
                        coordinates: [building.coordinates!.latitude, building.coordinates!.longitude],
                    });
                } else {
                    setPopupInfo({ id: building.key!, coordinates: [0, 0] });
                }
                // Reset flag after state updates
                setTimeout(() => {
                    isSelectingNewBuildingRef.current = false;
                }, 100);
            }}
            visible={matchesSearch(building)}
            ref={(el) => { listItemRefs.current[building.key!] = el; }}
            category1Label={locationCategories?.labels.category1 || 'Kategori 1'}
            category2Label={locationCategories?.labels.category2 || 'Kategori 2'}
        />
    );

    // ── Render ──────────────────────────────────────────────────
    return (
        <div className="map-overview-page-wrapper" style={{ display: 'flex', height: '100%' }}>
            {/* Building List - Only show filtered buildings */}
            <div className="map-sidebar">
                {/* Search bar + sort control */}
                <div className="map-sidebar-toolbar">
                    <div className="search-bar" style={{ position: 'relative', flex: 1, marginBottom: 0, paddingLeft: 0 }}>
                        <i className="pi pi-search search-icon" />
                        <input
                            type="text"
                            placeholder="Sök på byggnadsnamn"
                            value={searchTerm}
                            onChange={e => {
                                setSearchTerm(e.target.value);
                                setShouldRefocusMap(true);
                            }}
                        />
                    </div>
                    <div className="sort-control">
                        <CustomDropdown<SortOption>
                            options={sortOptions}
                            selectedKey={selectedSort}
                            onSelect={setSelectedSort}
                            showAllOption={false}
                            icon="pi pi-sort-alt"
                            compact={true}
                            titleOverride={SORT_SHORT_LABELS[selectedSort]}
                        />
                    </div>
                </div>

                {/* Everything below the toolbar scrolls as one region (see .map-sidebar-scroll). */}
                <div className="map-sidebar-scroll">
                {/* Filter panel - collapsible, gray pill summary when collapsed.
                    Building type/group filters don't depend on locationCategories,
                    so the panel renders even before (or without) that config. */}
                <div className="map-sidebar-filter">
                    <FilterPanel
                        locationCategories={locationCategories}
                        categoryOptions={categoryOptions}
                        selectedCategory1={selectedCategory1}
                        selectedCategory2={selectedCategory2}
                        selectedCategory3={selectedCategory3}
                        selectedCategory4={selectedCategory4}
                        selectedCategory5={selectedCategory5}
                        onSelectCategory={handleSelectCategory}
                        buildingTypeOptions={buildingTypeOptions}
                        selectedBuildingTypes={selectedBuildingTypes}
                        onBuildingTypesChange={handleBuildingTypesChange}
                        groupTagOptions={groupTagOptions}
                        selectedGroupTagKeys={selectedGroupTagKeys}
                        onGroupTagKeysChange={handleGroupTagKeysChange}
                        hasAnyFilters={hasAnyFilters}
                        onResetAllFilters={handleResetAllFilters}
                    />
                </div>

                <div className="map-sidebar-divider" />

                {/* Building list */}
                <ul className="map-building-list">
                    {buildingGroups
                        ? buildingGroups.map((section) => {
                            // Hide the whole section when the search hides every member,
                            // so no empty tag header lingers.
                            if (!section.buildings.some(matchesSearch)) return null;
                            return (
                                <React.Fragment key={section.key}>
                                    <li
                                        style={{
                                            listStyleType: 'none',
                                            fontWeight: 700,
                                            fontSize: '15px',
                                            color: '#333',
                                            padding: '4px 0',
                                        }}
                                    >
                                        {section.label}
                                    </li>
                                    {section.buildings.map((building) =>
                                        renderBuildingItem(building, `${section.key}:${building.key}`),
                                    )}
                                </React.Fragment>
                            );
                        })
                        : filteredBuildings.map((building) =>
                            renderBuildingItem(building, building.key!),
                        )}
                </ul>
                </div>
            </div>

            {/* Map - Show all buildings, but only focus on filtered ones */}
            {!isSmallScreen && (
            <div className={"map_container"} style={{visibility: 'visible'}} >
                <MapComponent
                    allCoordinates={allCoordinates}
                    focusCoordinates={filteredCoordinates}
                    highlightedId={highlightedKey}
                    popupInfo={popupInfo}
                    shouldResizeBounds={shouldRefocusMap}
                    buildingSummaries={buildings}
                    onMarkerClick={(key) => {
                        setShouldRefocusMap(false);
                        setHighlightedKey(key);
                        const building = buildings?.find((b) => b.key === key);
                        if (building?.coordinates) {
                            setPopupInfo({
                                id: key,
                                coordinates: [
                                    building.coordinates.latitude,
                                    building.coordinates.longitude,
                                ],
                            });
                        }
                    }}
                    onPopupClose={() => {
                        // Only clear selection if we're not selecting a new building
                        if (!isSelectingNewBuildingRef.current) {
                            setHighlightedKey('');
                        }
                        setPopupInfo(null);
                    }}
                />
            </div>
            )}
        </div>
    );
};

export default MapOverview;
